import { initializeApp, getApps, getApp, FirebaseApp } from 'firebase/app';
import { getFirestore, Firestore, connectFirestoreEmulator, doc, setDoc } from 'firebase/firestore';
import { getAuth, Auth, connectAuthEmulator, RecaptchaVerifier } from 'firebase/auth';
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getFunctions, Functions } from 'firebase/functions';

// Public web config for chorost-store-center. These values are meant to be in the
// client bundle (Firebase security is rules + authorized domains, not hiding the key).
// Hardcoded so a malformed/unread .env can never ship a broken production build again.
const PROD_FIREBASE_CONFIG = {
  apiKey: 'AIzaSyBPGFfJYc-GY0zM4Od-whL5fy6p-npH4Go',
  authDomain: 'chorost-store-center.firebaseapp.com',
  projectId: 'chorost-store-center',
  storageBucket: 'chorost-store-center.firebasestorage.app',
  messagingSenderId: '796806267166',
  appId: '1:796806267166:web:7373bc5cd9266ca3f8490f',
};

export const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || PROD_FIREBASE_CONFIG.apiKey,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || PROD_FIREBASE_CONFIG.authDomain,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || PROD_FIREBASE_CONFIG.projectId,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || PROD_FIREBASE_CONFIG.storageBucket,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || PROD_FIREBASE_CONFIG.messagingSenderId,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || PROD_FIREBASE_CONFIG.appId,
};

const missingConfigKeys = Object.entries(firebaseConfig)
  .filter(([, value]) => !value)
  .map(([key]) => key);

if (missingConfigKeys.length > 0) {
  // If we're in the browser and the env vars are missing, the most common cause is
  // a stale cached bundle still being served by the service worker after a deploy.
  // Self-heal: unregister SWs, nuke caches, then hard reload once. Subsequent loads
  // will fetch the fresh `index.html` and the new bundle hashes.
  if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
    const FLAG = '__fin_sw_self_heal_v2';
    try {
      const alreadyTried = sessionStorage.getItem(FLAG) === '1';
      if (!alreadyTried) {
        sessionStorage.setItem(FLAG, '1');
        console.warn(
          '[Firebase config] Missing keys:',
          missingConfigKeys,
          '— purging service worker / cache and reloading once.'
        );
        const purgeAndReload = async (): Promise<void> => {
          try {
            if ('serviceWorker' in navigator) {
              const regs = await navigator.serviceWorker.getRegistrations();
              await Promise.all(regs.map(r => r.unregister().catch(() => false)));
            }
          } catch {
            // ignore
          }
          try {
            if ('caches' in window) {
              const names = await caches.keys();
              await Promise.all(names.map(n => caches.delete(n).catch(() => false)));
            }
          } catch {
            // ignore
          }
          window.location.reload();
        };
        void purgeAndReload();
      }
    } catch {
      // sessionStorage may be unavailable; fall through and throw below.
    }
  }
  throw new Error(`Firebase config error: ${missingConfigKeys[0]} is missing`);
}

console.log('Initializing Firebase with config:', {
  ...firebaseConfig,
  apiKey: '***'
});

// Initialize Firebase immediately at declaration to avoid temporal dead zone issues
const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
console.log('✅ Firebase app initialized successfully');

const auth = getAuth(app);
console.log('✅ Firebase Auth initialized successfully');

const db: Firestore = getFirestore(app);
const storage = getStorage(app);
const functions = getFunctions(app);
console.log('✅ Firebase Storage, Firestore, and Functions initialized successfully');
console.log('[firebase/config] db type:', typeof db, 'truthy:', !!db, 'app:', !!app);
if (!db) {
  throw new Error('[firebase/config] CRITICAL: getFirestore(app) returned a falsy value. Firebase chunk may not have loaded.');
}

// Connect to emulators if in development mode
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true') {
  console.log('🔧 Connecting to Firebase emulators...');
  
  if (import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL) {
    try {
      connectAuthEmulator(auth, import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_URL);
      console.log('✅ Connected to Auth emulator');
    } catch (error) {
      console.warn('Auth emulator already connected or unavailable');
    }
  }
  
  if (import.meta.env.VITE_FIREBASE_FIRESTORE_EMULATOR_HOST) {
    try {
      const [host, portStr] = import.meta.env.VITE_FIREBASE_FIRESTORE_EMULATOR_HOST.split(':');
      const port = parseInt(portStr, 10);
      connectFirestoreEmulator(db, host, port);
      console.log('✅ Connected to Firestore emulator');
    } catch (error) {
      console.warn('Firestore emulator already connected or unavailable');
    }
  }
}

export const setupShopifyConfig = async (accessToken: string, storeUrl: string) => {
  try {
    await setDoc(doc(db, 'settings', 'api_keys'), {
      SHOPIFY_ACCESS_TOKEN: accessToken,
      SHOPIFY_STORE_URL: storeUrl,
    });
    return true;
  } catch (error) {
    console.error('Error setting up Shopify config:', error);
    return false;
  }
};

// Initialize reCAPTCHA verifier for phone authentication
export const initializeRecaptcha = (elementId: string): RecaptchaVerifier => {
  return new RecaptchaVerifier(auth, elementId, {
    size: 'invisible',
    callback: (response: any) => {
      console.log('reCAPTCHA solved:', response);
    },
    'expired-callback': () => {
      console.log('reCAPTCHA expired');
    }
  });
};

export { app, auth, db, storage, functions };

